import React, { useCallback } from 'react';
import { ChevronDown, ChevronUp, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@components/ui/button';
import { Card } from '@components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@components/ui/collapsible';
import { Tooltip } from '@components/tooltip';

interface GeneratedUrlPreviewProps {
    generatedUrl: string;
}

export const GeneratedUrlPreview: React.FC<GeneratedUrlPreviewProps> = ({
    generatedUrl
}) => {
    const [expanded, setExpanded] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const handleCopyClick = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(generatedUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL: ', err);
        }
    }, [generatedUrl]);

    const handleOpenInNewTab = useCallback(() => {
        window.open(generatedUrl, '_blank', 'noopener,noreferrer');
    }, [generatedUrl]);

    return (
        <Card className="mt-4 p-4">
            <Collapsible open={expanded} onOpenChange={setExpanded}>
                <div className="flex items-center justify-between gap-2">
                    <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-left text-sm font-medium">
                        Generated URL
                        {expanded ? (
                            <ChevronUp className="size-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="size-4 text-muted-foreground" />
                        )}
                    </CollapsibleTrigger>
                    <div className="flex gap-1">
                        <Tooltip
                            hideIcon
                            title={copied ? 'Copied!' : 'Copy URL'}
                        >
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Copy URL"
                                onClick={() => void handleCopyClick()}
                            >
                                <Copy
                                    className={
                                        copied ? 'text-success' : undefined
                                    }
                                />
                            </Button>
                        </Tooltip>
                        <Tooltip hideIcon title="Open in new tab">
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Open in new tab"
                                onClick={handleOpenInNewTab}
                            >
                                <ExternalLink />
                            </Button>
                        </Tooltip>
                    </div>
                </div>

                <CollapsibleContent>
                    <pre className="mt-2 max-h-[200px] overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-xs break-all whitespace-pre-wrap">
                        {generatedUrl}
                    </pre>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
};
